import { useState, useEffect } from "react";
import axios from "axios";
import {
  Briefcase, Plus, TrendingUp, TrendingDown, Trash2, RefreshCw,
  Loader2, Trophy, Target, Activity, ArrowUpRight, ArrowDownRight, FileText,
  DollarSign, Award, AlertTriangle, PlusCircle, Download,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from "recharts";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (v, digits = 2) => v == null || !Number.isFinite(Number(v))
  ? "—"
  : Number(v).toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });

export default function MyFund() {
  const [funds, setFunds] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [factsheetOpen, setFactsheetOpen] = useState(false);
  const [factsheet, setFactsheet] = useState(null);

  useEffect(() => { fetchFunds(); }, []);

  const fetchFunds = async () => {
    try {
      const { data } = await axios.get(`${API}/my-fund/list`);
      setFunds(data.funds || []);
      if ((data.funds || []).length && !selected) {
        loadFund(data.funds[0].id);
      }
    } catch (e) {
      toast.error("Failed to load funds");
    } finally {
      setLoading(false);
    }
  };

  const loadFund = async (fundId) => {
    try {
      const { data } = await axios.get(`${API}/my-fund/${fundId}`);
      setSelected(data);
    } catch (e) {
      toast.error("Failed to load fund");
    }
  };

  const deleteFund = async (fundId) => {
    if (!window.confirm("Archive this fund? Its data is kept but hidden.")) return;
    try {
      await axios.delete(`${API}/my-fund/${fundId}`);
      toast.success("Fund archived");
      setSelected(null);
      fetchFunds();
    } catch (e) {
      toast.error("Failed to archive fund");
    }
  };

  const openFactsheet = async () => {
    if (!selected) return;
    try {
      const { data } = await axios.get(`${API}/my-fund/${selected.id}/factsheet`);
      setFactsheet(data);
      setFactsheetOpen(true);
    } catch (e) {
      toast.error("Failed to load factsheet");
    }
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8 space-y-6" data-testid="my-fund-loading">
        <Skeleton className="h-8 w-72" />
        <div className="grid gap-4 md:grid-cols-3"><Skeleton className="h-32" /><Skeleton className="h-32" /><Skeleton className="h-32" /></div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-6 animate-slide-up" data-testid="my-fund-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight uppercase flex items-center gap-2" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
            <Briefcase className="w-7 h-7 text-amber-500" />
            My Fund
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Your own virtual fund ledger: manually log holdings and trades (from AuraTrade signals or your
            HL portfolios) and this tracks it like a real fund — NAV over time, Sharpe/Sortino, drawdown,
            win rate and a downloadable factsheet.
          </p>
        </div>
        <div className="flex gap-2">
          {selected && (
            <Button variant="outline" onClick={openFactsheet} data-testid="factsheet-btn">
              <FileText className="w-4 h-4 mr-2" /> Factsheet
            </Button>
          )}
          <CreateFundDialog open={createOpen} setOpen={setCreateOpen} onCreated={fetchFunds} />
        </div>
      </div>

      {/* Fund tabs / list */}
      {funds.length === 0 ? (
        <Card>
          <CardContent className="py-12 flex flex-col items-center gap-3 text-center">
            <Briefcase className="w-10 h-10 text-muted-foreground" />
            <div className="font-medium">No funds yet</div>
            <p className="text-sm text-muted-foreground max-w-md">
              Create a fund with a starting capital amount, then use "Add Holding" / "Record Trade" on it
              whenever you act on an AuraTrade setup or update an HL portfolio position. Videra computes the
              NAV history, risk metrics and win rate from what you log — nothing is auto-added.
            </p>
            <Button onClick={() => setCreateOpen(true)} data-testid="create-first-fund-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Fund
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="flex gap-2 overflow-x-auto pb-1" data-testid="fund-tabs">
            {funds.map(f => (
              <button
                key={f.id}
                onClick={() => loadFund(f.id)}
                data-testid={`fund-tab-${f.id}`}
                className={`px-4 py-2 rounded-lg border transition-all text-sm whitespace-nowrap ${
                  selected?.id === f.id
                    ? "bg-amber-500/20 border-amber-500/50 text-amber-300"
                    : "bg-slate-800/40 border-slate-700 hover:border-slate-500"
                }`}
              >
                {f.name}
                <span className="ml-2 text-xs text-muted-foreground">£{fmt(f.current_capital, 0)}</span>
              </button>
            ))}
          </div>
          {selected && <FundDetail fund={selected} onRefresh={() => loadFund(selected.id)} onDelete={() => deleteFund(selected.id)} />}
        </>
      )}

      {/* Factsheet dialog */}
      <FactsheetDialog open={factsheetOpen} setOpen={setFactsheetOpen} data={factsheet} />
    </div>
  );
}

function CreateFundDialog({ open, setOpen, onCreated }) {
  const [form, setForm] = useState({ name: "", description: "", initial_capital: 100000, target_return_pct: 15, management_style: "active" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/my-fund/create`, form);
      toast.success(`Fund "${form.name}" created`);
      setOpen(false);
      setForm({ name: "", description: "", initial_capital: 100000, target_return_pct: 15, management_style: "active" });
      onCreated();
    } catch (e) {
      toast.error("Failed to create fund");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="create-fund-btn"><Plus className="w-4 h-4 mr-2" />New Fund</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create New Fund</DialogTitle>
          <DialogDescription>Track a portfolio + swing trades as one hedge-fund NAV.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Name</Label>
            <Input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Videra Alpha Fund" data-testid="fund-name-input" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
              placeholder="Your investment thesis, strategy notes..." rows={2} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Initial Capital (£)</Label>
              <Input type="number" required value={form.initial_capital}
                onChange={e => setForm({ ...form, initial_capital: parseFloat(e.target.value) })}
                data-testid="fund-capital-input" />
            </div>
            <div>
              <Label>Target Return (annual %)</Label>
              <Input type="number" step="0.1" value={form.target_return_pct}
                onChange={e => setForm({ ...form, target_return_pct: parseFloat(e.target.value) })} />
            </div>
          </div>
          <div>
            <Label>Style</Label>
            <Select value={form.management_style} onValueChange={v => setForm({ ...form, management_style: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="passive">Passive</SelectItem>
                <SelectItem value="hybrid">Hybrid</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={busy} data-testid="submit-create-fund">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Create Fund
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function FundDetail({ fund, onRefresh, onDelete }) {
  const [addHoldingOpen, setAddHoldingOpen] = useState(false);
  const [addTradeOpen, setAddTradeOpen] = useState(false);
  const [updateNavOpen, setUpdateNavOpen] = useState(false);

  const totalReturn = fund.total_return_pct || 0;
  const isUp = totalReturn >= 0;

  return (
    <div className="space-y-6" data-testid={`fund-detail-${fund.id}`}>
      {/* Top stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <StatCard
          label="NAV"
          value={fmt(fund.current_nav, 2)}
          sub={`from ${fmt(fund.starting_nav, 2)}`}
          icon={Activity}
          testid="stat-nav"
        />
        <StatCard
          label="Capital"
          value={`£${fmt(fund.current_capital, 0)}`}
          sub={`initial £${fmt(fund.initial_capital, 0)}`}
          icon={DollarSign}
          testid="stat-capital"
        />
        <StatCard
          label="Total Return"
          value={`${isUp ? "+" : ""}${fmt(totalReturn, 2)}%`}
          sub={fund.high_water_mark ? `HWM ${fmt(fund.high_water_mark, 2)}` : ""}
          icon={isUp ? TrendingUp : TrendingDown}
          className={isUp ? "text-emerald-400" : "text-red-400"}
          testid="stat-return"
        />
        <StatCard
          label="Sharpe Ratio"
          value={fmt(fund.sharpe_ratio, 2)}
          sub={`Sortino ${fmt(fund.sortino_ratio, 2)}`}
          icon={Award}
          testid="stat-sharpe"
        />
      </div>

      {/* NAV chart */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">NAV History</CardTitle>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setUpdateNavOpen(true)} data-testid="update-nav-btn">
                <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Update NAV
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={(fund.nav_history || []).map(p => ({ ...p, date: new Date(p.date).toLocaleDateString() }))}>
              <defs>
                <linearGradient id="navGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="date" stroke="#64748b" fontSize={11} />
              <YAxis stroke="#64748b" fontSize={11} domain={["auto", "auto"]} />
              <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 8 }} />
              <Area type="monotone" dataKey="nav" stroke="#f59e0b" strokeWidth={2} fill="url(#navGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Holdings + Trades */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Holdings ({fund.holdings?.length || 0})</CardTitle>
              <Button size="sm" variant="outline" onClick={() => setAddHoldingOpen(true)} data-testid="add-holding-btn">
                <PlusCircle className="w-3.5 h-3.5 mr-1.5" /> Add
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {(fund.holdings || []).length === 0 && (
              <div className="text-sm text-muted-foreground text-center py-6">No holdings yet</div>
            )}
            {(fund.holdings || []).map((h, i) => (
              <div key={i} className="flex justify-between items-center p-2.5 rounded bg-slate-800/40 border border-slate-700/40">
                <div>
                  <div className="text-sm font-medium">{h.name}</div>
                  <div className="text-xs text-muted-foreground">{h.symbol || h.asset_type}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium">£{fmt(h.current_value, 0)}</div>
                  <div className="text-[11px] text-muted-foreground">qty {fmt(h.quantity, 4)}</div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Trades ({fund.trades?.length || 0}) · win rate {fmt(fund.win_rate, 1)}%</CardTitle>
              <Button size="sm" variant="outline" onClick={() => setAddTradeOpen(true)} data-testid="add-trade-btn">
                <PlusCircle className="w-3.5 h-3.5 mr-1.5" /> Record
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {(fund.trades || []).length === 0 && (
              <div className="text-sm text-muted-foreground text-center py-6">No trades yet</div>
            )}
            {(fund.trades || []).slice(0, 8).map((t, i) => (
              <div key={i} className="flex justify-between items-center p-2.5 rounded bg-slate-800/40 border border-slate-700/40">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={t.direction === "LONG" ? "text-emerald-400 border-emerald-500/40" : "text-red-400 border-red-500/40"}>
                    {t.direction === "LONG" ? <ArrowUpRight className="w-3 h-3 mr-1" /> : <ArrowDownRight className="w-3 h-3 mr-1" />}
                    {t.direction}
                  </Badge>
                  <span className="text-sm">{t.instrument}</span>
                  <Badge variant="outline" className="text-[10px]">{t.status}</Badge>
                </div>
                <div className="text-right">
                  {t.pnl != null && (
                    <div className={`text-sm font-medium ${t.pnl >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                      {t.pnl >= 0 ? "+" : ""}£{fmt(t.pnl, 2)}
                    </div>
                  )}
                  <div className="text-[11px] text-muted-foreground">@ {fmt(t.entry_price, 4)}</div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Danger zone */}
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={onDelete} className="text-red-400 border-red-500/40 hover:bg-red-500/10" data-testid="archive-fund-btn">
          <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Archive Fund
        </Button>
      </div>

      <AddHoldingDialog open={addHoldingOpen} setOpen={setAddHoldingOpen} fundId={fund.id} onDone={onRefresh} />
      <AddTradeDialog open={addTradeOpen} setOpen={setAddTradeOpen} fundId={fund.id} onDone={onRefresh} />
      <UpdateNavDialog open={updateNavOpen} setOpen={setUpdateNavOpen} fund={fund} onDone={onRefresh} />
    </div>
  );
}

function StatCard({ label, value, sub, icon: Icon, className = "", testid }) {
  return (
    <Card data-testid={testid}>
      <CardContent className="pt-6">
        <div className="flex justify-between items-start mb-2">
          <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
          {Icon && <Icon className="w-4 h-4 text-muted-foreground" />}
        </div>
        <div className={`text-2xl font-bold ${className}`}>{value}</div>
        {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
      </CardContent>
    </Card>
  );
}

function AddHoldingDialog({ open, setOpen, fundId, onDone }) {
  const [form, setForm] = useState({ asset_type: "fund", name: "", symbol: "", quantity: 1, cost_basis: 0, current_value: 0 });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/my-fund/${fundId}/add-holding`, { fund_id: fundId, ...form });
      toast.success("Holding added");
      setOpen(false);
      onDone();
    } catch (e) { toast.error("Failed to add holding"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogHeader><DialogTitle>Add Holding</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Type</Label>
              <Select value={form.asset_type} onValueChange={v => setForm({ ...form, asset_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fund">Fund</SelectItem>
                  <SelectItem value="stock">Stock / ETF</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Symbol</Label><Input value={form.symbol} onChange={e => setForm({ ...form, symbol: e.target.value })} placeholder="AAPL, VUAG..." /></div>
          </div>
          <div><Label>Name</Label><Input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Quantity</Label><Input type="number" step="0.0001" value={form.quantity} onChange={e => setForm({ ...form, quantity: parseFloat(e.target.value) })} /></div>
            <div><Label>Cost Basis (£)</Label><Input type="number" step="0.01" value={form.cost_basis} onChange={e => setForm({ ...form, cost_basis: parseFloat(e.target.value) })} /></div>
            <div><Label>Current Value (£)</Label><Input type="number" step="0.01" value={form.current_value} onChange={e => setForm({ ...form, current_value: parseFloat(e.target.value) })} /></div>
          </div>
          <DialogFooter><Button type="submit"><PlusCircle className="w-4 h-4 mr-2" />Add</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AddTradeDialog({ open, setOpen, fundId, onDone }) {
  const [form, setForm] = useState({ instrument: "", direction: "LONG", entry_price: 0, exit_price: "", position_size: 1, status: "OPEN" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = {
        fund_id: fundId, ...form,
        exit_price: form.exit_price === "" ? null : parseFloat(form.exit_price),
        entry_price: parseFloat(form.entry_price),
        position_size: parseFloat(form.position_size),
      };
      // If exit set, compute pnl
      if (body.exit_price != null) {
        const signed = form.direction === "LONG" ? 1 : -1;
        body.pnl = (body.exit_price - body.entry_price) * signed * body.position_size;
        body.status = "CLOSED";
      }
      await axios.post(`${API}/my-fund/${fundId}/record-trade`, body);
      toast.success("Trade recorded");
      setOpen(false);
      onDone();
    } catch (e) { toast.error("Failed to record trade"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogHeader><DialogTitle>Record Trade</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Instrument</Label><Input required value={form.instrument} onChange={e => setForm({ ...form, instrument: e.target.value })} placeholder="ES=F, XAUUSD" /></div>
            <div><Label>Direction</Label>
              <Select value={form.direction} onValueChange={v => setForm({ ...form, direction: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="LONG">LONG</SelectItem>
                  <SelectItem value="SHORT">SHORT</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Entry</Label><Input type="number" step="0.0001" required value={form.entry_price} onChange={e => setForm({ ...form, entry_price: e.target.value })} /></div>
            <div><Label>Exit (blank = open)</Label><Input type="number" step="0.0001" value={form.exit_price} onChange={e => setForm({ ...form, exit_price: e.target.value })} /></div>
            <div><Label>Size</Label><Input type="number" step="0.01" value={form.position_size} onChange={e => setForm({ ...form, position_size: e.target.value })} /></div>
          </div>
          <DialogFooter><Button type="submit"><PlusCircle className="w-4 h-4 mr-2" />Record</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function UpdateNavDialog({ open, setOpen, fund, onDone }) {
  const [capital, setCapital] = useState(fund.current_capital);
  useEffect(() => { setCapital(fund.current_capital); }, [fund.current_capital]);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/my-fund/${fund.id}/update-nav`, { current_capital: parseFloat(capital) });
      toast.success("NAV updated");
      setOpen(false);
      onDone();
    } catch (e) { toast.error("Failed to update NAV"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogHeader><DialogTitle>Update NAV</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Current Total Capital (£)</Label>
            <Input type="number" step="0.01" required value={capital} onChange={e => setCapital(e.target.value)} data-testid="update-nav-input" />
            <p className="text-xs text-muted-foreground mt-1">
              NAV will be recalculated automatically. Starting NAV: {fmt(fund.starting_nav, 2)} on £{fmt(fund.initial_capital, 0)}.
            </p>
          </div>
          <DialogFooter><Button type="submit"><RefreshCw className="w-4 h-4 mr-2" />Update</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function FactsheetDialog({ open, setOpen, data }) {
  if (!data) return null;
  const perf = data.performance || {};
  const risk = data.risk_metrics || {};
  const stats = data.trading_stats || {};
  const download = () => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${data.fund_name.replace(/\s+/g, "_")}_factsheet.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>{data.fund_name} — Factsheet</span>
            <Button size="sm" variant="outline" onClick={download} data-testid="download-factsheet-btn">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Download JSON
            </Button>
          </DialogTitle>
          <DialogDescription>Generated {new Date(data.generated_at).toLocaleString()}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <Section title="Performance">
            <Kv k="Current NAV" v={fmt(perf.current_nav, 2)} />
            <Kv k="Starting NAV" v={fmt(perf.starting_nav, 2)} />
            <Kv k="High Water Mark" v={fmt(perf.high_water_mark, 2)} />
            <Kv k="Total Return" v={`${fmt(perf.total_return_pct, 2)}%`} />
            <Kv k="Current Capital" v={`£${fmt(perf.current_capital, 0)}`} />
          </Section>
          <Section title="Risk Metrics">
            <Kv k="Sharpe Ratio" v={fmt(risk.sharpe_ratio, 2)} />
            <Kv k="Sortino Ratio" v={fmt(risk.sortino_ratio, 2)} />
            <Kv k="Max Drawdown" v={`${fmt(risk.max_drawdown_pct, 2)}%`} />
          </Section>
          <Section title="Trading Statistics">
            <Kv k="Total Trades" v={stats.total_trades || 0} />
            <Kv k="Win Rate" v={`${fmt(stats.win_rate_pct, 1)}%`} />
            <Kv k="Wins / Losses" v={`${stats.winning_trades || 0} / ${stats.losing_trades || 0}`} />
            <Kv k="Total P&L" v={`£${fmt(stats.total_pnl, 2)}`} />
            <Kv k="Profit Factor" v={fmt(stats.profit_factor, 2)} />
          </Section>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }) {
  return (
    <div className="border border-slate-700/50 rounded-lg overflow-hidden">
      <div className="px-3 py-2 bg-slate-800/60 text-xs uppercase tracking-wider text-amber-400 font-semibold">{title}</div>
      <div className="p-3 space-y-1.5">{children}</div>
    </div>
  );
}

function Kv({ k, v }) {
  return (
    <div className="flex justify-between text-xs">
      <span className="text-muted-foreground">{k}</span>
      <span className="font-medium">{v}</span>
    </div>
  );
}
